import { combine, withContext, withAsyncEffect, withCallbacks, withState } from 'react-hooks-combine'
import { StoreContext } from 'store/context'
import { ServicesContext } from 'services/context'

import TournamentTeamsTabComponent from './component'

export const TournamentTeamsTab = combine({
	hooks: [
		withContext('teamsStore', StoreContext, ({ teams }) => teams),
		withContext('eventsStore', StoreContext, ({ events }) => events),
		withContext('teamService', ServicesContext, ({ teamService }) => teamService),
		withContext('followerService', ServicesContext, ({ followerService }) => followerService),

		// Add local state for teams to enable immediate UI updates
		withState('localTeams', 'setLocalTeams', []),

		// Load teams that are actually playing in this tournament
		withAsyncEffect({
			deps: ['tournamentId'],
			dataName: 'teams',
			loadingName: 'loading',
			errorName: 'error',
			async asyncAction({ teamService, eventsStore, teamsStore, followerService, setLocalTeams }, { tournamentId }) {
				if (!tournamentId) {
					setLocalTeams([])
					return []
				}

				try {
					// Load games for this tournament to find participating teams
					const currentDate = Date.now()
					const { events } = await eventsStore.load({
						tournamentId,
						size: 100,
						page: 0,
						sort: 'startTime,asc',
						startTimeEqOrGreater: currentDate,
						resolvedStatus: 'NOT_RESOLVED'
					})

					// Extract unique team IDs from games
					const participatingTeamIds = new Set()
					events?.forEach(event => {
						if (event.teamOne?.id) participatingTeamIds.add(event.teamOne.id)
						if (event.teamTwo?.id) participatingTeamIds.add(event.teamTwo.id)
					})

					if (participatingTeamIds.size === 0) {
						setLocalTeams([])
						return []
					}

					// Load full team data for participating teams
					const teams = []
					for (const teamId of participatingTeamIds) {
						try {
							const team = await teamsStore.loadOne(teamId)
							if (team) teams.push(team)
						} catch (error) {
							console.warn('Failed to load team by ID:', teamId, error.message)
						}
					}

					if (teams.length === 0) {
						setLocalTeams([])
						return []
					}

					// Load followed status for teams
					try {
						const followedTeams = await followerService.getTeamFollowers({
							size: 1000,
							page: 0
						})

						const followedTeamIds = new Set(
							followedTeams?.content?.map(team => team.teamDTO?.id || team.id) || []
						)

						// Update teams with followed status
						teams.forEach(team => {
							team.followed = followedTeamIds.has(team.id)
						})
					} catch (error) {
						console.warn('Failed to load team follow status:', error)
						// Set default followed status to false
						teams.forEach(team => {
							team.followed = false
						})
					}

					// Update local state to trigger re-render
					setLocalTeams([...teams])
					return teams

				} catch (error) {
					console.error('Failed to load teams:', error)
					setLocalTeams([])
					throw new Error('Failed to load teams')
				}
			},
		}),

		withCallbacks({
			onToggleFollow: ({ followerService, localTeams, setLocalTeams }) => async (team) => {
				try {
					const isCurrentlyFollowed = team.followed

					if (isCurrentlyFollowed) {
						await followerService.removeFollowers({ teamIds: [team.id] })
					} else {
						await followerService.addFollowers({ teamIds: [team.id] })
					}

					// Update local state immediately for instant UI feedback
					const updatedTeams = localTeams.map(t =>
						t.id === team.id ? { ...t, followed: !isCurrentlyFollowed } : t
					)
					setLocalTeams(updatedTeams)

				} catch (error) {
					console.error('Failed to toggle team follow status:', error)
				}
			},
		}, ['localTeams', 'setLocalTeams']),
	],
})(TournamentTeamsTabComponent) 